<script lang="ts">
  // Early voting (SEC-reported counts; the separate `model` block is an estimate and wears the purple Est. look).
  // variant: hero (before results) · compact (card once results flow) · full (Turnout page).
  import type { DayPoint, EarlyVote } from '../../types';
  import { fmtInt } from '../../lib/format';
  import { electionYear, listText } from '../../lib/race';
  import { app, ds, resource } from '../../lib/app.svelte';
  import Est from '../atoms/Est.svelte';
  import ValueMap from './ValueMap.svelte';

  let { ev, scope, variant = 'compact' }: { ev: EarlyVote; scope: 'charleston' | 'statewide'; variant?: 'hero' | 'compact' | 'full' } = $props();
  const ei = $derived(ev.early_in_person);
  const thisYear = $derived(electionYear(app.meta) ?? 2026);
  /** votes added for the unreported final day(s); renamed from early_estimated_missing_days (it always held votes) */
  const missingVotes = $derived(ev.model ? (ev.model.early_estimated_missing_votes ?? (ev.model as { early_estimated_missing_days?: number }).early_estimated_missing_days ?? 0) : 0);
  const chs = $derived(scope === 'charleston');
  const place = $derived(chs ? 'Charleston County' : 'South Carolina');
  const count = $derived(ei ? (chs ? ei.by_county.find((c) => c.county === 'Charleston')?.count ?? null : ei.statewide) : null);
  const mail = $derived(ev.mail ? (chs ? ev.mail.by_county.find((c) => c.county === 'Charleston') ?? null : ev.mail.statewide) : null);
  const reg = $derived(ev.registration ? (chs ? ev.registration.by_county.find((c) => c.county === 'Charleston')?.registered ?? null : ev.registration.statewide) : null);
  const same = (y: '2022' | '2024') => ev.history[y]?.same_point?.[scope];
  const vs22 = $derived(count !== null && same('2022') ? count / same('2022')! - 1 : null);
  const vs24 = $derived(count !== null && same('2024') ? count / same('2024')! : null);
  const est = $derived(ev.model ? ev.model.early_expected_final[scope] : null);
  const md = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  const pct = (x: number) => `${Math.round(x * 100)}%`;

  // cumulative-by-day chart
  const series = $derived.by(() => {
    const out: { y: string; pts: DayPoint[]; c: string; w: number }[] = [];
    if (ev.history['2022'] && thisYear !== 2022) out.push({ y: '2022', pts: ev.history['2022'].by_day[scope], c: 'var(--ink-4)', w: 2 });
    if (ev.history['2024'] && thisYear !== 2024) out.push({ y: '2024', pts: ev.history['2024'].by_day[scope], c: 'var(--ink-3)', w: 2 });
    if (ei) out.push({ y: String(thisYear), pts: ei.by_day[scope], c: 'var(--ink)', w: 3.5 });
    return out;
  });
  const others = $derived(series.filter((s) => s.y !== String(thisYear)).map((s) => s.y));
  let W = $state(0);
  const CH = 200, L = 52, R = 56, T = 12, B = 26;
  const maxDay = $derived(Math.max(12, ...series.flatMap((s) => s.pts.map((p) => p.day))));
  const rawMax = $derived(Math.max(1, ...series.flatMap((s) => s.pts.map((p) => p.cumulative))));
  const ystep = $derived([1, 2, 2.5, 5].flatMap((m) => [1e3, 1e4, 1e5, 1e6].map((e) => m * e)).sort((a, b) => a - b).find((s) => rawMax / s <= 4) ?? 1e6);
  const maxY = $derived(Math.ceil((rawMax * 1.04) / ystep) * ystep);
  const X = (d: number) => L + ((d - 1) / Math.max(1, maxDay - 1)) * Math.max(10, W - L - R);
  const Y = (v: number) => T + (1 - v / maxY) * (CH - T - B);
  const yt = $derived(Array.from({ length: Math.round(maxY / ystep) + 1 }, (_, i) => i * ystep));
  const compactN = (n: number) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${Math.round(n / 1e3)}K` : String(n));

  // full variant: county map (early votes as % of registration) + Charleston sites + by-county table
  const geo = resource(() => (variant === 'full' ? ds().geoLayer('sc_counties') : null));
  const share = $derived(new Map((ei?.by_county ?? []).map((c) => [c.county, c.count / (ev.registration?.by_county.find((r) => r.county === c.county)?.registered ?? NaN)] as const).filter(([, v]) => Number.isFinite(v))));
  const sites = $derived(ei?.charleston_sites ?? []);
  const siteMax = $derived(Math.max(1, ...sites.map((s) => s.count)));
</script>

<section class="card ev" class:hero={variant === 'hero'}>
  <div class="hd">
    <div>
      <div class="kicker">Early voting · {place}</div>
      <h2>{variant === 'hero' ? 'Early voting so far' : 'Early voting'}</h2>
      {#if variant === 'hero'}<p class="lede">Until the first results come in, these are the only real numbers — reported by the S.C. Election Commission.</p>{/if}
    </div>
  </div>

  {#if ei && count !== null}
    <div class="tiles">
      <div class="tile big">
        <div class="tv num">{fmtInt(count)}</div>
        <div class="tk">Early in-person votes</div>
        <div class="ts">through {md(ei.through_date)} · {ei.final ? 'final' : `${ei.days_reported} of ${ei.days_total} days`}</div>
      </div>
      {#if mail}
        <div class="tile"><div class="tv num">{fmtInt(mail.returned)}</div><div class="tk">Mail ballots returned</div><div class="ts">of {fmtInt(mail.issued)} issued ({pct(mail.returned / Math.max(1, mail.issued))})</div></div>
      {/if}
      {#if vs22 !== null || vs24 !== null}
        <div class="tile">
          {#if vs22 !== null}<div class="tv num" class:up={vs22 >= 0}>{vs22 >= 0 ? '+' : '−'}{Math.abs(Math.round(vs22 * 100))}%</div><div class="tk">vs 2022 at this point</div>{/if}
          {#if vs24 !== null}<div class="ts">{pct(vs24)} of 2024 at this point</div>{/if}
        </div>
      {/if}
      {#if reg !== null}
        <div class="tile"><div class="tv num">{pct((count + (mail?.returned ?? 0)) / reg)}</div><div class="tk">of registered voters have voted</div><div class="ts">{fmtInt(reg)} registered</div></div>
      {/if}
    </div>
    {#if est !== null && ev.model && !ei.final}
      <div class="estline" title={ev.model.explanation}>
        <Est /> <span>Estimated final early vote including the last day{missingVotes ? ' (not yet reported)' : ''}: <b>≈{fmtInt(est)}</b></span>
      </div>
    {/if}
  {:else}
    <p class="muted">Early-voting figures haven’t been published yet.</p>
  {/if}

  {#if variant !== 'compact' && series.length && ei}
    <div class="chart" bind:clientWidth={W}>
      <div class="kicker">Early votes by day of early voting</div>
      {#if W}
        <svg viewBox="0 0 {W} {CH}" width={W} height={CH} role="img" aria-label="Cumulative early votes by day, {thisYear}{others.length ? ` compared with ${listText(others)}` : ''}">
          {#each yt as v}<line x1={L} x2={W - R} y1={Y(v)} y2={Y(v)} class="g" /><text x={L - 8} y={Y(v) + 4} text-anchor="end" class="a">{compactN(v)}</text>{/each}
          {#each Array.from({ length: maxDay }, (_, i) => i + 1) as d}{#if d === 1 || d % 2 === 0}<text x={X(d)} y={CH - 6} text-anchor="middle" class="a">{d === 1 ? 'Day 1' : d}</text>{/if}{/each}
          {#each series as s (s.y)}
            <path d={s.pts.map((p, i) => `${i ? 'L' : 'M'}${X(p.day).toFixed(1)},${Y(p.cumulative).toFixed(1)}`).join('')} fill="none" stroke={s.c} stroke-width={s.w} stroke-linejoin="round" stroke-linecap="round" />
            {#if s.pts.length}{@const p = s.pts.at(-1)!}<circle cx={X(p.day)} cy={Y(p.cumulative)} r="4" fill={s.c} stroke="var(--card)" stroke-width="2" /><text x={X(p.day) + 8} y={Y(p.cumulative) + 4} class="l" style="fill:{s.c}">{s.y}</text>{/if}
          {/each}
        </svg>
      {/if}
    </div>
  {/if}

  {#if variant === 'full'}
    <div class="full">
      {#if geo.value && share.size}
        <div><div class="kicker">Early votes as a share of registered voters</div><ValueMap geo={geo.value} values={share} fmt={(v) => pct(v)} label="Early in-person votes ÷ registered" /></div>
      {/if}
      {#if sites.length}
        <div>
          <div class="kicker">Charleston early-voting sites</div>
          <ol class="sites">
            {#each sites as s}<li><span class="sn">{s.site}</span><span class="sb"><i style="width:{(100 * s.count) / siteMax}%"></i></span><b class="num">{fmtInt(s.count)}</b></li>{/each}
          </ol>
        </div>
      {/if}
    </div>
    <details class="disclosure tbl">
      <summary>Early votes by county</summary>
      <table>
        <thead><tr><th>County</th><th class="n">Early in person</th><th class="n">Mail returned</th><th class="n">Registered</th><th class="n">Voted early</th></tr></thead>
        <tbody>
          {#each [...ei!.by_county].sort((a, b) => b.count - a.count) as c (c.county)}
            {@const m = ev.mail?.by_county.find((x) => x.county === c.county)}
            {@const rg = ev.registration?.by_county.find((x) => x.county === c.county)?.registered}
            <tr><td>{c.county}</td><td class="n num">{fmtInt(c.count)}</td><td class="n num">{m ? fmtInt(m.returned) : '—'}</td><td class="n num">{rg ? fmtInt(rg) : '—'}</td><td class="n num">{rg ? pct((c.count + (m?.returned ?? 0)) / rg) : '—'}</td></tr>
          {/each}
        </tbody>
      </table>
    </details>
  {/if}

  <p class="src">Source: <a href={ei?.source_url ?? 'https://scvotes.gov'} rel="noopener" target="_blank">S.C. Election Commission</a>{ei ? ` · report of ${md(ei.report_date)}` : ''}{ev.mail ? ` · mail as of ${md(ev.mail.as_of)}` : ''}. Day N = Nth day of the 12-day early-voting period.</p>
</section>

<style>
  .ev { display: flex; flex-direction: column; gap: 16px; }
  .ev.hero { border: 1px solid var(--line-2); }
  .hd h2 { font-size: 1.45rem; margin-top: 6px; }
  .hero .hd h2 { font-size: clamp(1.6rem, 1.2rem + 1.6vw, 2.2rem); }
  .tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 12px; }
  .tile { border: 1px solid var(--line); border-radius: 14px; padding: 14px 16px; background: var(--card-2); }
  .tile.big { background: var(--card); border: 1.5px solid var(--ink); }
  .tv { font: 700 1.9rem/1.05 var(--display); letter-spacing: -0.02em; }
  .big .tv { font-size: clamp(2.1rem, 1.6rem + 1.8vw, 2.9rem); }
  .tv.up { color: var(--good); }
  .tk { font-weight: 600; font-size: 0.86rem; margin-top: 6px; }
  .ts { font-size: 0.8rem; color: var(--ink-3); margin-top: 2px; }
  .estline { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; background: var(--est-bg); border: 1px solid var(--est-line); color: var(--est-ink); border-radius: 12px; padding: 10px 14px; font-size: 0.9rem; }
  .chart { margin-top: 4px; }
  svg { display: block; width: 100%; height: auto; overflow: visible; margin-top: 8px; }
  .g { stroke: var(--line); stroke-width: 1; }
  .a { font: 11px var(--font); fill: var(--ink-3); font-variant-numeric: tabular-nums; }
  .l { font: 700 12px var(--font); }
  .full { display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr); gap: 24px; align-items: start; }
  .sites { list-style: none; margin: 10px 0 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
  .sites li { display: grid; grid-template-columns: minmax(0, 1fr) 90px auto; gap: 10px; align-items: center; font-size: 0.84rem; }
  .sn { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .sb { height: 8px; background: var(--card-3); border-radius: 999px; overflow: hidden; }
  .sb i { display: block; height: 100%; background: var(--c-turnout); border-radius: 999px; }
  .tbl table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 0.85rem; }
  .tbl th { text-align: left; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.06em; color: var(--ink-3); padding: 8px 6px; border-bottom: 1px solid var(--line); }
  .tbl td { padding: 7px 6px; border-bottom: 1px solid var(--line); }
  .n { text-align: right; }
  .src { margin: 0; font-size: 0.78rem; color: var(--ink-3); }
  @media (max-width: 800px) { .full { grid-template-columns: minmax(0, 1fr); } }
</style>
